import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  untracked,
} from '@angular/core';
import { TagEditorComponent, TagEditorItem } from '@tourfold/libraries/shared-ui';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { SystemTagsCache } from './system-tags-cache.service';

/**
 * Input renderer for a Tags relation. Wraps `tf-tag-editor` in editable mode and emits the new
 * selection back through the same string-id contract the generic `RelationInputComponent` uses,
 * so the parent form is unchanged.
 *
 * **Inline create is delegated to the host.** When the user clicks "Create '<query>'" in the
 * picker's autocomplete, this component emits `createNewRequested(query)`. The host
 * (`InstanceFormComponent`) catches it, consults the Tags bundle for `createView` +
 * `createMode`, and mounts the Tag create UI inside its breadcrumb body — no modal-on-modal.
 * The picker stays single-purpose: search and select. The create UX lives in `TagFormSection`.
 *
 * Value contract:
 *  - `cardinality === 'MANY'` (the realistic case): `value` is a `string[]` of tag IDs and
 *    `valueChange` emits a `string[]`.
 *  - `cardinality === 'ONE'`: `value` is a single `string` and `valueChange` emits the first
 *    selected id (or `null` when cleared). Supported for safety even though Tags is m:m today.
 */
@Component({
  selector: 'tf-tags-relation-input',
  standalone: true,
  imports: [TagEditorComponent],
  templateUrl: './tags-relation-input.component.html',
  styleUrl: './tags-relation-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagsRelationInputComponent implements OnInit {
  private readonly cache = inject(SystemTagsCache);
  private readonly access = inject(ResourceAccessService);

  readonly value = input<unknown>();
  readonly invalid = input<boolean>(false);
  readonly cardinality = input<'ONE' | 'MANY'>('MANY');

  @Output() readonly valueChange = new EventEmitter<string[] | string | null>();
  /**
   * Emitted when the user picks the "Create '<query>'" suggestion in the autocomplete. The
   * host catches this and opens the Tag create UI inside its own dialog (typically as a
   * breadcrumb swap). Carries the typed query so the create form can prefill the name.
   */
  @Output() readonly createNewRequested = new EventEmitter<string | undefined>();

  /**
   * Inline create suggestion is gated on the `tags:create` action. Without it the autocomplete
   * still shows existing tags but suppresses the "Create '<query>'" suggestion, so read-only
   * users can still tag instances without seeing a forbidden affordance.
   */
  protected readonly canCreate = computed(() => this.access.canPerform('tags', 'create'));

  private readonly selectedIds = computed<readonly string[]>(() => normalizeIds(this.value()));

  protected readonly selectedTags = computed<readonly TagEditorItem[]>(() => {
    const byId = this.tagsById();
    return this.selectedIds()
      .map((id) => byId.get(id))
      .filter((t): t is TagEditorItem => t !== undefined);
  });

  protected readonly availableTags = computed<readonly TagEditorItem[]>(() =>
    Array.from(this.tagsById().values())
  );

  private tagsById(): Map<string, TagEditorItem> {
    const byId = new Map<string, TagEditorItem>();
    for (const t of this.cache.tags()) {
      if (t.id === undefined) continue;
      byId.set(t.id, { id: t.id, name: t.name ?? '', color: t.color ?? '#9CA3AF' });
    }
    return byId;
  }

  ngOnInit(): void {
    void this.cache.ensureLoaded();
  }

  constructor() {
    // Self-healing cache refresh: when the host pushes a new id into `value` (typically right
    // after the instance-form's bundle create flow finishes), the cache may not yet contain
    // that tag. We detect the gap and trigger a background refresh so the picker can render
    // the new chip without the host needing to know anything about caches.
    effect(() => {
      const ids = this.selectedIds();
      if (ids.length === 0) return;
      const known = new Set(this.cache.tags().map((t) => t.id));
      const hasUnknown = ids.some((id) => !known.has(id));
      if (hasUnknown) {
        untracked(() => void this.cache.refresh());
      }
    });
  }

  protected onTagsChange(next: TagEditorItem[]): void {
    const ids = next.map((t) => t.id);
    if (this.cardinality() === 'ONE') {
      this.valueChange.emit(ids[0] ?? null);
    } else {
      this.valueChange.emit(ids);
    }
  }

  protected onSearchOpened(): void {
    void this.cache.refresh();
  }

  protected onCreateRequested(query: string): void {
    this.createNewRequested.emit(query);
  }
}

function normalizeIds(value: unknown): readonly string[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  if (typeof value === 'string') return value.length > 0 ? [value] : [];
  return [];
}
